import { ArrowRight, Calendar, Gauge, Wallet } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { stashPrefill } from "@/lib/smooth";
import { canoneCard, anticipoCard, conIva, fmtEuro } from "@/lib/prezzi";

export const OfferCard = ({ offer, linkToModel = true, ivaInclusa = false }) => {
  const url = `/offerte/${offer.slug_marca}/${offer.slug_modello}/`;
  const navigate = useNavigate();
  const canoneBase = canoneCard(offer);
  const canone = canoneBase != null && ivaInclusa ? conIva(canoneBase) : canoneBase;
  const anticipo = anticipoCard(offer);

  // il tasto porta sempre alla pagina dell'offerta, con form in evidenza e precompilato
  const onCta = (e) => {
    e.preventDefault();
    stashPrefill(`${offer.marca} ${offer.modello} ${offer.allestimento}`);
    navigate(`${url}#preventivo`);
  };

  return (
    <article
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
      data-testid={`offer-card-${offer.slug_marca}-${offer.slug_modello}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {offer.foto ? (
          <img
            src={offer.foto}
            alt={`${offer.marca} ${offer.modello} a noleggio lungo termine`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2.5 bg-xmove-cream" data-testid="offer-photo-placeholder">
            <img src="/images/logo.png" alt="" aria-hidden="true" className="h-12 w-12 opacity-30" />
            <span className="text-xs font-semibold uppercase tracking-widest text-xmove-muted">Foto in arrivo</span>
          </div>
        )}
        {offer.badge && (
          <span className="absolute left-4 top-4 rounded-full bg-xmove-orange px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-soft">
            {offer.badge}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-xmove-muted">{offer.marca}</p>
        <h3 className="mt-1 font-display text-xl font-bold text-xmove-ink">
          {offer.modello} <span className="font-normal text-xmove-muted text-base">{offer.allestimento}</span>
        </h3>
        <div className="mt-4 flex items-baseline gap-1.5">
          {canone != null ? (
            <>
              {offer.n_varianti > 1 && <span className="text-sm text-xmove-muted">da</span>}
              <span className="font-display text-3xl font-bold text-xmove-orange">€ {canone}</span>
              <span className="text-sm text-xmove-muted">{ivaInclusa ? "/mese IVA inclusa" : "/mese + IVA"}</span>
            </>
          ) : (
            <span className="font-display text-xl font-bold text-xmove-orange">Canone su richiesta</span>
          )}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-xmove-ink/8 pt-4 text-center">
          <div>
            <Wallet size={15} className="mx-auto text-xmove-muted" />
            <p className="mt-1 text-[11px] text-xmove-muted">Anticipo</p>
            <p className="text-[13px] font-bold text-xmove-ink">{anticipo != null ? fmtEuro(anticipo) : "—"}</p>
          </div>
          <div>
            <Calendar size={15} className="mx-auto text-xmove-muted" />
            <p className="mt-1 text-[11px] text-xmove-muted">Durata</p>
            <p className="text-[13px] font-bold text-xmove-ink">{offer.durata_mesi != null ? `${offer.durata_mesi} mesi` : "—"}</p>
          </div>
          <div>
            <Gauge size={15} className="mx-auto text-xmove-muted" />
            <p className="mt-1 text-[11px] text-xmove-muted">Km/anno</p>
            <p className="text-[13px] font-bold text-xmove-ink">{offer.km_annui != null ? `${offer.km_annui.toLocaleString("it-IT")}` : "—"}</p>
          </div>
        </div>
        <div className="mt-5 flex flex-1 items-end gap-2">
          {linkToModel && (
            <Link
              to={url}
              data-testid={`offer-details-${offer.slug_modello}`}
              className="flex-1 rounded-xl border border-xmove-ink/15 py-3 text-center text-sm font-semibold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange"
            >
              Dettagli
            </Link>
          )}
          <button
            onClick={onCta}
            data-testid={`offer-cta-${offer.slug_modello}`}
            className="relative flex flex-[1.4] items-center justify-center rounded-xl bg-xmove-orange py-3 text-sm font-bold text-white transition-all hover:bg-xmove-orangeDark active:scale-[0.98]"
          >
            <span className="px-4 text-center">Richiedi questa offerta</span>
            <ArrowRight size={15} className="absolute right-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
};
